import { CookieConsent } from '@/context/CookieConsentStore'
import { captureInternalTrafficFromLocation, isInternalTraffic } from './internalTraffic'

const ATTRIBUTION_STORAGE_KEY = 'kibu_attribution_context_v1'
const ANONYMOUS_ID_STORAGE_KEY = 'kibu_anonymous_id_v1'
const SESSION_ID_STORAGE_KEY = 'kibu_session_id_v1'

type AttributionContext = {
    utmSource?: string
    utmMedium?: string
    utmCampaign?: string
    utmTerm?: string
    utmContent?: string
    gclid?: string
    fbclid?: string
    kibuClickId?: string
    affiliateCode?: string
    affiliateSlug?: string
    landingPath?: string
    firstSeenAt?: string
}

export type CheckoutTrackingContext = {
    utmSource?: string
    utmMedium?: string
    utmCampaign?: string
    utmTerm?: string
    utmContent?: string
    gclid?: string
    fbclid?: string
    fbp?: string
    fbc?: string
    gaClientId?: string
    eventSourceUrl?: string
    landingPath?: string
    sessionId?: string
    anonymousId?: string
    kibuClickId?: string
    affiliateCode?: string
    affiliateSlug?: string
    initiateCheckoutEventId?: string
    purchaseEventId?: string
    /** Set on browsers marked as Kibu staff (see internalTraffic). */
    internalTraffic?: boolean
    consent?: {
        analytics: boolean
        marketing: boolean
    }
}

const generateTrackingId = (prefix: string): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return `${prefix}_${crypto.randomUUID()}`
    }

    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
}

const readStoredValue = (storageKey: string, storage: Storage | undefined): string | undefined => {
    if (!storage) {
        return undefined
    }

    try {
        const value = storage.getItem(storageKey)
        return value || undefined
    } catch {
        return undefined
    }
}

const writeStoredValue = (storageKey: string, value: string, storage: Storage | undefined): void => {
    if (!storage) {
        return
    }

    try {
        storage.setItem(storageKey, value)
    } catch {
        // Best-effort only.
    }
}

const getPersistentAnonymousId = (): string | undefined => {
    if (typeof window === 'undefined') {
        return undefined
    }

    const existing = readStoredValue(ANONYMOUS_ID_STORAGE_KEY, window.localStorage)
    if (existing) {
        return existing
    }

    const next = generateTrackingId('anon')
    writeStoredValue(ANONYMOUS_ID_STORAGE_KEY, next, window.localStorage)
    return next
}

const getSessionId = (): string | undefined => {
    if (typeof window === 'undefined') {
        return undefined
    }

    const existing = readStoredValue(SESSION_ID_STORAGE_KEY, window.sessionStorage)
    if (existing) {
        return existing
    }

    const next = generateTrackingId('session')
    writeStoredValue(SESSION_ID_STORAGE_KEY, next, window.sessionStorage)
    return next
}

export type EventTrackingContext = Omit<CheckoutTrackingContext, 'purchaseEventId'> & {
    eventId?: string
}

const initialConsent: CookieConsent = {
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
    hasConsented: false,
}

const getCookieValue = (name: string): string | undefined => {
    if (typeof document === 'undefined') {
        return undefined
    }

    const escapedName = name.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')
    const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`))
    return match ? decodeURIComponent(match[1]) : undefined
}

const readStoredAttribution = (): AttributionContext => {
    if (typeof window === 'undefined') {
        return {}
    }

    try {
        const raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY)
        if (!raw) {
            return {}
        }
        const parsed = JSON.parse(raw)
        if (!parsed || typeof parsed !== 'object') {
            return {}
        }
        return parsed
    } catch {
        return {}
    }
}

const writeStoredAttribution = (value: AttributionContext): void => {
    if (typeof window === 'undefined') {
        return
    }

    try {
        window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(value))
    } catch {
        // Best-effort storage only.
    }
}

export const persistAttributionContext = (patch: Partial<AttributionContext>): void => {
    if (typeof window === 'undefined') {
        return
    }

    const stored = readStoredAttribution()
    const next: AttributionContext = {
        ...stored,
        ...patch,
        landingPath: patch.landingPath || stored.landingPath || `${window.location.pathname}${window.location.search}`,
        firstSeenAt: stored.firstSeenAt || new Date().toISOString(),
    }
    writeStoredAttribution(next)
}

const getGaClientIdFromCookie = (): string | undefined => {
    const gaCookie = getCookieValue('_ga')
    if (!gaCookie) {
        return undefined
    }

    // Typical formats:
    // GA1.1.1234567890.1234567890
    // GS1.1.1234567890.1.1.1234567890.0.0.0
    const parts = gaCookie.split('.')
    if (parts.length >= 4 && parts[0].startsWith('GA')) {
        return `${parts[2]}.${parts[3]}`
    }

    return undefined
}

const buildFbc = (fbclid?: string): string | undefined => {
    const existingFbc = getCookieValue('_fbc')
    if (existingFbc) {
        return existingFbc
    }

    if (!fbclid) {
        return undefined
    }

    return `fb.1.${Date.now()}.${fbclid}`
}

const isLocalHostname = (hostname: string): boolean => (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '::1'
)

const getEventSourceUrl = (): string | undefined => {
    if (typeof window === 'undefined') {
        return undefined
    }

    const configuredSiteUrl = process.env.REACT_APP_PUBLIC_SITE_URL || process.env.REACT_APP_SHOP_SITE_URL
    if (!isLocalHostname(window.location.hostname)) {
        return window.location.href
    }

    if (!configuredSiteUrl) {
        return undefined
    }

    try {
        const url = new URL(configuredSiteUrl)
        url.pathname = window.location.pathname
        url.search = window.location.search
        url.hash = ''
        return url.toString()
    } catch {
        return undefined
    }
}

export const readCookieConsentSnapshot = (): CookieConsent => {
    if (typeof window === 'undefined') {
        return initialConsent
    }

    try {
        const raw = window.localStorage.getItem('cookie-consent-storage')
        if (!raw) {
            return initialConsent
        }
        const parsed = JSON.parse(raw)
        const consent = parsed?.state?.consent
        if (!consent || typeof consent !== 'object') {
            return initialConsent
        }
        return {
            necessary: Boolean(consent.necessary),
            analytics: Boolean(consent.analytics),
            marketing: Boolean(consent.marketing),
            preferences: Boolean(consent.preferences),
            hasConsented: Boolean(consent.hasConsented),
        }
    } catch {
        return initialConsent
    }
}

// Email click context is only a hint. Checkout verifies its recipient and
// current permission on the backend before hiding the marketing choice.
export const getEmailClickId = (): string | undefined => {
    if (typeof window === 'undefined') return undefined
    return new URLSearchParams(window.location.search).get('kibu_click_id') || readStoredAttribution().kibuClickId
}

export const captureAttributionFromLocation = (): void => {
    if (typeof window === 'undefined') {
        return
    }

    captureInternalTrafficFromLocation()
    const params = new URLSearchParams(window.location.search)
    const stored = readStoredAttribution()

    const next: AttributionContext = {
        ...stored,
        utmSource: params.get('utm_source') || stored.utmSource,
        utmMedium: params.get('utm_medium') || stored.utmMedium,
        utmCampaign: params.get('utm_campaign') || stored.utmCampaign,
        utmTerm: params.get('utm_term') || stored.utmTerm,
        utmContent: params.get('utm_content') || stored.utmContent,
        gclid: params.get('gclid') || stored.gclid,
        fbclid: params.get('fbclid') || stored.fbclid,
        kibuClickId: params.get('kibu_click_id') || stored.kibuClickId,
        affiliateCode: params.get('aff') || params.get('affiliate') || params.get('affiliate_code') || params.get('ref') || stored.affiliateCode,
        affiliateSlug: params.get('affiliate_slug') || stored.affiliateSlug,
        landingPath: stored.landingPath || `${window.location.pathname}${window.location.search}`,
        firstSeenAt: stored.firstSeenAt || new Date().toISOString(),
    }

    writeStoredAttribution(next)
}

export const buildCheckoutTrackingContext = (
    consent: CookieConsent,
    purchaseEventId: string
): CheckoutTrackingContext => {
    if (typeof window === 'undefined') {
        return {
            purchaseEventId,
            consent: {
                analytics: consent.analytics,
                marketing: consent.marketing,
            },
        }
    }

    const stored = readStoredAttribution()
    const fbp = getCookieValue('_fbp')
    const fbc = buildFbc(stored.fbclid)

    return {
        utmSource: stored.utmSource,
        utmMedium: stored.utmMedium,
        utmCampaign: stored.utmCampaign,
        utmTerm: stored.utmTerm,
        utmContent: stored.utmContent,
        gclid: stored.gclid,
        fbclid: stored.fbclid,
        fbp,
        fbc,
        gaClientId: getGaClientIdFromCookie(),
        eventSourceUrl: getEventSourceUrl(),
        landingPath: stored.landingPath,
        sessionId: getSessionId(),
        anonymousId: getPersistentAnonymousId(),
        kibuClickId: stored.kibuClickId,
        affiliateCode: stored.affiliateCode,
        affiliateSlug: stored.affiliateSlug,
        ...(isInternalTraffic() ? { internalTraffic: true } : {}),
        purchaseEventId,
        consent: {
            analytics: consent.analytics,
            marketing: consent.marketing,
        },
    }
}

export const buildEventTrackingContext = (
    consent: CookieConsent,
    eventId?: string
): EventTrackingContext => {
    if (typeof window === 'undefined') {
        return {
            eventId,
            consent: {
                analytics: consent.analytics,
                marketing: consent.marketing,
            },
        }
    }

    const stored = readStoredAttribution()
    const fbp = getCookieValue('_fbp')
    const fbc = buildFbc(stored.fbclid)

    return {
        utmSource: stored.utmSource,
        utmMedium: stored.utmMedium,
        utmCampaign: stored.utmCampaign,
        utmTerm: stored.utmTerm,
        utmContent: stored.utmContent,
        gclid: stored.gclid,
        fbclid: stored.fbclid,
        fbp,
        fbc,
        gaClientId: getGaClientIdFromCookie(),
        eventSourceUrl: getEventSourceUrl(),
        landingPath: stored.landingPath,
        sessionId: getSessionId(),
        anonymousId: getPersistentAnonymousId(),
        kibuClickId: stored.kibuClickId,
        affiliateCode: stored.affiliateCode,
        affiliateSlug: stored.affiliateSlug,
        ...(isInternalTraffic() ? { internalTraffic: true } : {}),
        eventId,
        consent: {
            analytics: consent.analytics,
            marketing: consent.marketing,
        },
    }
}
